import { BookOpen, Leaf, Landmark } from 'lucide-react';
import { useState } from 'react';
import { text, type Locale } from '../shared/content';
import type { Character, GameEvent } from '../shared/types';
import { backgroundStoryCopy } from '../shared/narrative-copy';
const copy = {
  article: text('Read article', '查看文章', '查看文章', 'Leer artículo'),
  share: text('Copy article link', '复制文章链接', '複製文章連結', 'Copiar enlace'),
  copied: text('Article link copied', '文章链接已复制', '文章連結已複製', 'Enlace copiado'),
  copyFailed: text(
    'Open the article to copy its address',
    '请打开文章复制地址',
    '請開啟文章複製網址',
    'Abre el artículo para copiar su dirección',
  ),
  personal: text('Your city memories', '你的城市记忆', '你的城市記憶', 'Tus recuerdos de la ciudad'),
  city: text('From the city desk', '城市公告', '城市公告', 'Noticias de la ciudad'),
  paycheck: text(
    'A shift finished. Earned',
    '完成一班工作，获得劳动所得',
    '完成一班工作，獲得勞動所得',
    'Un turno terminado. Has ganado',
  ),
  shop: text(
    'Your café opened its doors.',
    '你的咖啡店开门了。',
    '你的咖啡店開門了。',
    'Tu cafetería ha abierto sus puertas.',
  ),
  help: text(
    'You lent a hand to the riverfront project.',
    '你为河岸项目出了一份力。',
    '你為河岸專案出了一份力。',
    'Has ayudado al proyecto de la ribera.',
  ),
  project: text(
    'The neighborhood completed its riverfront project.',
    '街区共同完成了河岸项目。',
    '街區共同完成了河岸專案。',
    'El barrio ha completado el proyecto de la ribera.',
  ),
  vacancyElection: text(
    'A five-day vacancy election opened for available offices.',
    '空缺职位已开启五天补选。',
    '空缺職位已開啟五天補選。',
    'Se ha abierto una elección de cinco días para las vacantes.',
  ),
  election: text(
    'An in-game election has been counted. View the city hall for results.',
    '游戏内选举已计票，结果可在市政面板查看。',
    '遊戲內選舉已計票，結果可在市政面板查看。',
    'Se han contado los votos del juego. Consulta el ayuntamiento.',
  ),
  vacancy: text(
    'A city office is vacant. Basic services continue under the published rules.',
    '市政职位出现空缺，基础服务按公开规则继续。',
    '市政職位出現空缺，基礎服務按公開規則繼續。',
    'Hay un cargo municipal vacante. Los servicios básicos continúan.',
  ),
  policy: text(
    'An approved city policy took effect.',
    '一项已批准的城市政策开始生效。',
    '一項已批准的城市政策開始生效。',
    'Ha entrado en vigor una política aprobada.',
  ),
  transit: text(
    'Transit support returned to its base level because treasury funds were insufficient.',
    '财政不足，交通补贴已回到基础档位。',
    '財政不足，交通補貼已回到基礎檔位。',
    'La ayuda de transporte ha vuelto al nivel básico por falta de fondos.',
  ),
  pause: text(
    'Community publishing is temporarily paused for platform review. Normal life services continue.',
    '平台审核期间暂缓社区发布，日常生活服务继续。',
    '平台審核期間暫緩社群發布，日常生活服務繼續。',
    'Las publicaciones están pausadas para revisión. La vida cotidiana continúa.',
  ),
};
function eventText(event: GameEvent, character: Character, locale: Locale) {
  switch (event.kind) {
    case 'arrival':
      return backgroundStoryCopy[character.data.background].welcome[locale];
    case 'paycheck':
      return `${copy.paycheck[locale]} C$${Number(event.payload.amount) || 0}.`;
    case 'shop-opened':
      return copy.shop[locale];
    case 'community-help':
      return copy.help[locale];
    case 'project-completed':
      return copy.project[locale];
    case 'vacancy-election-opened':
      return copy.vacancyElection[locale];
    case 'election-result':
      return copy.election[locale];
    case 'seats-vacated':
    case 'official-resigned':
      return copy.vacancy[locale];
    case 'policy-enacted':
      return copy.policy[locale];
    case 'transit-caretaker':
      return copy.transit[locale];
    case 'operational-pause':
      return copy.pause[locale];
    default:
      return null;
  }
}
export default function CityJournal({
  events,
  character,
  locale,
  kind,
}: {
  events: GameEvent[];
  character: Character;
  locale: Locale;
  kind: 'personal' | 'city';
}) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copyFailedId, setCopyFailedId] = useState<string | null>(null);
  const items = events
    .filter((e) =>
      kind === 'personal'
        ? e.actorId === character.id && e.visibility === 'private'
        : e.visibility === 'city' &&
          [
            'vacancy-election-opened',
            'project-completed',
            'election-result',
            'seats-vacated',
            'official-resigned',
            'policy-enacted',
            'transit-caretaker',
            'operational-pause',
          ].includes(e.kind),
    )
    .map((event) => ({ event, body: eventText(event, character, locale) }))
    .filter((e) => e.body)
    .slice(0, 6);
  if (!items.length) return null;
  return (
    <section className="city-journal">
      <h3>
        {kind === 'personal' ? <BookOpen size={17} /> : <Landmark size={17} />} {copy[kind][locale]}
      </h3>
      {items.map(({ event, body }) => (
        <article key={event.id}>
          <Leaf size={13} />
          <div>
            <p>{body}</p>
            <time dateTime={event.createdAt}>
              {new Intl.DateTimeFormat(locale, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              }).format(new Date(event.createdAt))}
            </time>
            {kind === 'city' &&
              ['project-completed', 'election-result', 'policy-enacted', 'vacancy-election-opened'].includes(
                event.kind,
              ) && (
                <div className="friend-actions">
                  <a href={`/news/${event.id}?lang=${locale}`} target="_blank" rel="noopener noreferrer">
                    {copy.article[locale]}
                  </a>
                  <button
                    className="text-button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(
                          `${location.origin}/news/${event.id}?lang=${locale}`,
                        );
                        setCopiedId(event.id);
                        setCopyFailedId(null);
                      } catch {
                        setCopyFailedId(event.id);
                      }
                    }}
                  >
                    {copiedId === event.id ? copy.copied[locale] : copy.share[locale]}
                  </button>
                  {copyFailedId === event.id && <small role="status">{copy.copyFailed[locale]}</small>}
                </div>
              )}
          </div>
        </article>
      ))}
    </section>
  );
}
